import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApiBuilder,
  AppConfigService,
  HttpHeadersPayload,
  HttpRequestMethods,
  IDP_APP,
  ReplaceItem,
  handleError
} from '@com-delta-idp/mnx-utils';
import { Observable } from 'rxjs';
import { X_EAM_AUTH_STATE } from '../../../../constants/login-remember-me-constant';
import {
  EndpointKey,
  MFA_OAUTH_URL
} from '../../../../constants/multi-factor-authentication-constant';
import {
  LoginAuthorizationOperationTypes,
  LoginOAuthResponse
} from '../../../../models/login-remember-me.interfaces';
import { LoginOAuthParams } from '../../../../models/oauth.interfaces';
import { UtilService } from '../../../util-service/util-service';

@Injectable({
  providedIn: 'root'
})
export class LoginAuthorizationService {
  _errorHandler = handleError.bind(this);

  constructor(
    private http: HttpClient,
    private utils: UtilService,
    private appConfig: AppConfigService
  ) {}

  getEamHeaderNameEnv(loginOAuthParams: LoginOAuthParams): string {
    if (
      loginOAuthParams.oauthConfig &&
      loginOAuthParams.oauthConfig.X_EAM_AUTHN_STATE_ENV
    )
      return loginOAuthParams.oauthConfig.X_EAM_AUTHN_STATE_ENV;
    else return X_EAM_AUTH_STATE;
  }

  loginAuthorization = (
    loginOAuthParams: LoginOAuthParams,
    eamAuthStateValue?: string
  ): Observable<LoginOAuthResponse> => {
    let initiateAuthorizationHeader: HttpHeadersPayload = {};
    if (eamAuthStateValue && eamAuthStateValue !== '') {
      const eamHeaderName = this.getEamHeaderNameEnv(loginOAuthParams);
      initiateAuthorizationHeader = {
        'Content-Type':
          'application/vnd.pingidentity.initiateOAuthAuthorization+json',
        'X-XSRF-Header': 'PingFederate',
        [eamHeaderName]: eamAuthStateValue,
        rejectUnauthorized: 'false'
      };
    } else {
      initiateAuthorizationHeader = {
        'Content-Type':
          'application/vnd.pingidentity.initiateOAuthAuthorization+json',
        'X-XSRF-Header': 'PingFederate',
        rejectUnauthorized: 'false'
      };
    }

    const loginAuthorizationUrl: ReplaceItem[] = [
      {
        src: MFA_OAUTH_URL,
        target: this.getUrlBasedOnStorageValue(loginOAuthParams)
      }
    ];

    return new ApiBuilder<undefined, LoginOAuthResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APP.COMMON)
      .endpointKey(EndpointKey.MFA_OAUTH_URL)
      .overrideEndpoint(loginAuthorizationUrl)
      .method(HttpRequestMethods['GET_HEADERS'])
      .timeOut(loginOAuthParams.oauthConfig.LOGIN_TIMEOUT ?? 10000)
      .httpHeaders(initiateAuthorizationHeader)
      .errorHandler(this._errorHandler)
      .library('LOGIN_AUTHORIZATION_SERVICE')
      .withCredentials(true)
      .create();
  };

  getUrlBasedOnStorageValue(loginOAuthParams: LoginOAuthParams): string {
    const encryptedTokenStored = this.utils.getLocalStorageValue('encyToken');
    const uniqueIdentifierStored = this.utils.getLocalStorageValue('uid');
    if (encryptedTokenStored && uniqueIdentifierStored) {
      if (loginOAuthParams.prompt) {
        return this.getLoginAuthorizationUrl(loginOAuthParams, 'PROMPT');
      } else {
        return this.getLoginAuthorizationUrl(loginOAuthParams, 'DEFAULT');
      }
    }
    return this.getLoginAuthorizationUrl(loginOAuthParams, 'DEFAULT');
  }

  private getLoginAuthorizationUrl(
    loginOAuthParams: LoginOAuthParams,
    operationType: LoginAuthorizationOperationTypes
  ): string {
    const initiateSsoSessionUrl = {
      PROMPT: this.utils.getPingServiceUrl(
        loginOAuthParams?.oauthConfig,
        '',
        loginOAuthParams?.pkceCode?.codeChallenge
      ).loginOAuthUrlPersistantRememberMe,
      DEFAULT: this.utils.getPingServiceUrl(
        loginOAuthParams?.oauthConfig,
        '',
        loginOAuthParams?.pkceCode?.codeChallenge
      ).loginOAuthUrlRememberMe
    };
    return initiateSsoSessionUrl[operationType];
  }
}
